import React from "react"
import Link from "../../adapters/Link"
import { usePathname } from "../../adapters/navigation"
import { useEffect, useRef, useState } from "react"
import {
  ArrowUpRight,
  ChevronDown,
  Globe2,
  Mail,
  Menu,
  Phone,
  SearchCheck,
  X,
} from "lucide-react"
import { CONTACT, NAV } from "../../lib/site"
import { T, langFromPath, switchPath } from "../../lib/i18n"
import { SmartLink } from "./SmartLink"
export function SiteHeader() {
  const pathname = usePathname()
  const lang = langFromPath(pathname)
  const t = T[lang]
  const nav = t.nav ?? NAV
  const other = lang === "fr" ? "en" : "fr"
  const [open, setOpen] = useState(false)
  const [openGroup, setOpenGroup] = useState(null)
  const header = useRef(null)
  const menuButton = useRef(null)
  const focusFirst = useRef(false)
  const active = href => {
    const current = pathname
      .replace(/\/$/, "")
      .replace(/\/contact\/complaint$/, "/contact/feedback")
      .replace(/\/certification\/benefits$/, "/certification/procedure")
      .replace(/\/about\/locations$/, "/about")
    return !!href && !href.includes("#") && current === href.replace(/\/$/, "")
  }
  const groupActive = items => items.some(item => active(item.to))
  useEffect(() => {
    setOpen(false)
    setOpenGroup(null)
  }, [pathname])
  useEffect(() => {
    const dismiss = event => {
      if (!header.current?.contains(event.target)) {
        setOpen(false)
        setOpenGroup(null)
      }
    }
    const escape = event => {
      if (event.key !== "Escape") return
      if (open) menuButton.current?.focus()
      else if (openGroup)
        document
          .getElementById(
            `desktop-trigger-${nav.findIndex(
              group => group.label === openGroup
            )}`
          )
          ?.focus()
      setOpen(false)
      setOpenGroup(null)
    }
    document.addEventListener("pointerdown", dismiss)
    document.addEventListener("keydown", escape)
    return () => {
      document.removeEventListener("pointerdown", dismiss)
      document.removeEventListener("keydown", escape)
    }
  }, [open, openGroup, nav])
  useEffect(() => {
    if (!openGroup || !focusFirst.current) return
    document
      .querySelector(
        `#desktop-nav-${nav.findIndex(group => group.label === openGroup)} a`
      )
      ?.focus()
    focusFirst.current = false
  }, [openGroup, nav])
  const links = items =>
    items.map(item => (
      <li key={item.label}>
        <SmartLink
          item={item}
          onClick={() => {
            setOpen(false)
            setOpenGroup(null)
          }}
          className={`site-dropdown-link${active(item.to) ? " is-active" : ""}`}
        >
          <span>
            <span className="site-dropdown-label">{item.label}</span>
            {item.desc && (
              <span className="site-dropdown-description">{item.desc}</span>
            )}
          </span>
          <ArrowUpRight className="size-4 shrink-0" aria-hidden />
        </SmartLink>
      </li>
    ))
  return (
    <header ref={header} className="site-header">
      <div className="site-header-utility">
        <div className="site-header-contact">
          <a href={CONTACT.phoneHref}>
            <Phone aria-hidden />
            {CONTACT.phone}
          </a>
          <a href={`mailto:${CONTACT.email}`}>
            <Mail aria-hidden />
            {CONTACT.email}
          </a>
        </div>
        <Link href={`/${lang}/contact/check`}>
          <SearchCheck aria-hidden />
          {t.check}
          <ArrowUpRight aria-hidden />
        </Link>
      </div>
      <div className="site-header-bar">
        <Link
          href={`/${lang}`}
          className="site-brand"
          aria-label={`Halal Correct France — ${t.home}`}
        >
          <img src="/images/hcc-logo.png" alt="" width={48} height={42} />
          <span className="site-brand-wordmark">
            <span>Halal Correct</span>
            <span>France</span>
          </span>
        </Link>
        <nav aria-label={t.mainNav} className="site-desktop-nav">
          <ul>
            <li>
              <Link
                href={`/${lang}`}
                className="site-nav-control"
                aria-current={active(`/${lang}`) ? "page" : undefined}
              >
                {t.home}
              </Link>
            </li>
            {nav.map((group, index) => (
              <li
                key={group.label}
                className="site-nav-group"
                onMouseLeave={() => setOpenGroup(null)}
                onBlur={event => {
                  if (!event.currentTarget.contains(event.relatedTarget))
                    setOpenGroup(null)
                }}
              >
                <button
                  id={`desktop-trigger-${index}`}
                  type="button"
                  className={`site-nav-control${
                    groupActive(group.items) ? " is-active" : ""
                  }`}
                  aria-expanded={openGroup === group.label}
                  aria-controls={`desktop-nav-${index}`}
                  onMouseEnter={() => setOpenGroup(group.label)}
                  onClick={event =>
                    setOpenGroup(
                      event.detail === 0 && openGroup === group.label
                        ? null
                        : group.label
                    )
                  }
                  onKeyDown={event => {
                    if (event.key === "ArrowDown") {
                      event.preventDefault()
                      if (openGroup === group.label)
                        document
                          .querySelector(`#desktop-nav-${index} a`)
                          ?.focus()
                      else {
                        focusFirst.current = true
                        setOpenGroup(group.label)
                      }
                    }
                  }}
                >
                  {group.label}
                  <ChevronDown className="size-3.5" aria-hidden />
                </button>
                {openGroup === group.label && (
                  <div className="site-dropdown">
                    <ul id={`desktop-nav-${index}`}>{links(group.items)}</ul>
                  </div>
                )}
              </li>
            ))}
            <li>
              <Link
                href={`/${lang}/blog`}
                className="site-nav-control"
                aria-current={active(`/${lang}/blog`) ? "page" : undefined}
              >
                {t.news}
              </Link>
            </li>
          </ul>
        </nav>
        <div className="site-header-actions">
          <a
            href={switchPath(pathname, other)}
            hrefLang={other}
            lang={other}
            className="site-language"
            aria-label={
              other === "en" ? "English version" : "Version française"
            }
          >
            <Globe2 className="size-3.5" aria-hidden />
            <span>{other}</span>
          </a>
          <Link href={`/${lang}/contact/apply`} className="site-header-apply">
            {t.apply}
            <ArrowUpRight className="size-4" aria-hidden />
          </Link>
          <button
            ref={menuButton}
            type="button"
            className="site-menu-toggle"
            aria-label={open ? t.menuClose : t.menuOpen}
            aria-expanded={open}
            aria-controls="site-mobile-navigation"
            onClick={() => {
              setOpen(!open)
              setOpenGroup(null)
            }}
          >
            {open ? (
              <X className="size-5" aria-hidden />
            ) : (
              <Menu className="size-5" aria-hidden />
            )}
          </button>
        </div>
      </div>
      {open && (
        <nav
          id="site-mobile-navigation"
          aria-label={t.mobileNav}
          className="site-mobile-nav"
        >
          <Link
            href={`/${lang}`}
            onClick={() => setOpen(false)}
            className="site-mobile-top-link"
            aria-current={active(`/${lang}`) ? "page" : undefined}
          >
            {t.home}
            <ArrowUpRight className="size-4" aria-hidden />
          </Link>
          {nav.map((group, index) => (
            <div key={group.label} className="site-mobile-group">
              <button
                type="button"
                className={`site-mobile-top-link${
                  groupActive(group.items) ? " is-active" : ""
                }`}
                aria-expanded={openGroup === group.label}
                aria-controls={`mobile-nav-${index}`}
                onClick={() =>
                  setOpenGroup(openGroup === group.label ? null : group.label)
                }
              >
                {group.label}
                <ChevronDown className="size-4" aria-hidden />
              </button>
              {openGroup === group.label && (
                <ul id={`mobile-nav-${index}`} className="site-mobile-sublinks">
                  {links(group.items)}
                </ul>
              )}
            </div>
          ))}
          <Link
            href={`/${lang}/blog`}
            onClick={() => setOpen(false)}
            className="site-mobile-top-link"
            aria-current={active(`/${lang}/blog`) ? "page" : undefined}
          >
            {t.news}
            <ArrowUpRight className="size-4" aria-hidden />
          </Link>
          <div className="site-mobile-shortcuts">
            <Link
              href={`/${lang}/contact/check`}
              onClick={() => setOpen(false)}
              className="site-mobile-check"
            >
              <SearchCheck className="size-4" aria-hidden />
              {t.check}
              <ArrowUpRight className="ml-auto size-4" aria-hidden />
            </Link>
            <Link
              href={`/${lang}/contact/apply`}
              onClick={() => setOpen(false)}
              className="site-header-apply"
            >
              {t.apply}
              <ArrowUpRight className="size-4" aria-hidden />
            </Link>
          </div>
          <div className="site-mobile-contact">
            <a href={CONTACT.phoneHref}>
              <Phone className="size-3.5" aria-hidden />
              {CONTACT.phone}
            </a>
            <a href={`mailto:${CONTACT.email}`}>{CONTACT.email}</a>
          </div>
        </nav>
      )}
    </header>
  )
}
